// https://github.com/inditex/app-zenit/blob/develop/apis/rest/zenit/zenit-api.yml

/**
 * Brand of the event
 * Must be one of: za, zh, md, oy, bk, pb, lf, st
 */
type Tenant = 'md';

/**
 * Name of the application that is sending the request
 * Examples: com.inditex.zara.iphone
 */
type AppName = 'com.inditex.massimodutti';

/**
 * Platform that is sending the request
 */
type Platform = 'web';

/**
 * Consent given by the user
 * IMPORTANT No user information can be sent for the following markets [China-CN]
 *
 * @property {string} consentCategories
 * @property {string} [consentedDate]
 * @property {string} [tcString]
 * @property {string} [addtlString]
 * @property {string} [USPrivacyString]
 */
export interface PrivacyConsent {
    consentCategories: string;
    consentedDate?: string;
    tcString?: string;
    addtlString?: string;
    USPrivacyString?: string;
}

/**
 * User agent detailed information
 *
 * @property {AppName} appName
 * @property {string} appVersion
 * @property {Platform} platform
 * @property {string} uaString
 */
export interface UserAgent {
    appName: AppName;
    appVersion: string;
    platform: Platform;
    uaString?: string;
    UAITXID?: string;
}

/**
 * Google campaigns parámeters
 *
 * @property {string} source
 * @property {string} [medium]
 * @property {string} [campaign]
 * @property {string} [term]
 * @property {string} [content]
 * @property {string} [gclid]
 */
export interface Utm {
    source: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
    gclid?: string;
}

/**
 * @property {string} country
 * @property {string} [region]
 * @property {number} [regionGroupId]
 */
export interface Geo {
    country: string;
    region?: string;
    regionGroupId?: number;
}

/**
 * Device Operating System name if available, possible values: android, ios, ipados, linux, mac, windows
 */
export enum Os {
    android = 'android',
    ios = 'ios',
    ipados = 'ipados',
    linux = 'linux',
    mac = 'mac',
    windows = 'windows',
}

/**
 * Device types
 */
export enum Type {
    mobile = 'mobile',
    desktop = 'desktop',
    tablet = 'tablet',
}

/**
 * User device information sent with the event
 *
 * @property {Type} type
 * @property {Os} os
 * @property {string} [name]
 * @property {string} [language]
 * @property {string} [osVersion]
 * @property {string} [firebaseId]
 */
export interface Device {
    clientId: string;
    firebaseId?: string;
    language?: string;
    name?: string;
    os: Os;
    osVersion?: string;
    type: Type;
}

/**
 * Client identification generated by Inditex
 *
 * @property {string} userId
 * @property {string} token
 * @property {string} kind
 */
export interface Identity {
    userId?: string;
    token?: string;
    kind?: string;
}

export interface TechnicalData {
    navigation: {
        url: string;
    };
}

export interface Referrer {
    url: string;
}

/**
 * Common fields in different events
 *
 * @property {PrivacyConsent} privacy-consent
 * @property {Device} device
 * @property {Tenant} tenant
 * @property {UserAgent} user-agent
 * @property {Record<string, any>} [extra]
 * @property {Geo} [geo]
 * @property {Utm} [utm]
 */
export interface BasicEvent {
    'privacy-consent': PrivacyConsent;
    'user-agent': UserAgent;
    device: Device;
    extra?: Record<string, any>;
    geo?: Geo;
    identity: Identity;
    projectKey: string;
    technical: TechnicalData;
    tenant: Tenant;
    utm?: Utm;
    referrer?: Referrer;
}
